import * as joint from "@joint/core";
import {AvoidLib} from "libavoid-js";

export class VerticePoint {
  x: number;
  y: number;
}

const defaultPin = 1;

export class AvoidRouter {

  private avoidRouter: any;
  private shapeRefs: Record<string, any> = {};
  private edgeRefs: Record<string, any> = {};
  private pinIds: Record<string, number> = {};
  private linksByPointer: Record<string, joint.dia.Link> = {};
  private connDirections: Record<string, any>;
  private margin: number;
  private id = 100000;
  public ignoreVerticesSet = false;

  // Routing deferred until the page has been drawn (see routeAllSoon)
  private routingScheduled = false;
  private destroyed = false;
  // Told when the links start and stop waiting for their routes
  public onRoutingChange: (routing: boolean) => void;

  protected graph: joint.dia.Graph;
  protected paper: joint.dia.Paper;

  static async load(wasmPath?: string) {
    if (wasmPath) {
      await AvoidLib.load(wasmPath);
    } else {
      await AvoidLib.load();
    }
  }

  static async setup() {
    async function initLibavoid() {
      await AvoidLib.load();
      const Avoid = AvoidLib.getInstance();
      return Avoid;
    }
    await initLibavoid();
  }

  public getGraph() {
    return this.graph;
  }

  public getPaper() {
    return this.paper;
  }

  constructor(graph: joint.dia.Graph, options: {
    shapeBufferDistance?: number,
    idealNudgingDistance?: number,
    paper?: joint.dia.Paper
  } = {}) {

    const Avoid = AvoidLib.getInstance();

    this.graph = graph;

    this.connDirections = {
      // @ts-ignore
      top: Avoid.ConnDirUp,
      // @ts-ignore
      right: Avoid.ConnDirRight,
      // @ts-ignore
      bottom: Avoid.ConnDirDown,
      // @ts-ignore
      left: Avoid.ConnDirLeft,
      // @ts-ignore
      all: Avoid.ConnDirAll,
    };

    const {
      shapeBufferDistance = 15,
      idealNudgingDistance = 10,
    } = options;

    this.margin = shapeBufferDistance;

    const router = new Avoid.Router(Avoid.OrthogonalRouting);

    /* Prevent links to be too close to shapes that they are not connected to */
    // @ts-ignore
    router.setRoutingParameter(Avoid.shapeBufferDistance, shapeBufferDistance);

    /* Allows more bends */
    // @ts-ignore
    router.setRoutingParameter(Avoid.segmentPenalty, 10);
    // @ts-ignore
    router.setRoutingParameter(Avoid.anglePenalty, 10);

    /* Disable expensive options for large graphs */
    // @ts-ignore
    router.setRoutingOption(Avoid.performUnifyingNudgingPreprocessingStep, false); // use false to speed-up
    // @ts-ignore
    router.setRoutingParameter(Avoid.idealNudgingDistance, idealNudgingDistance);

    // Penalise paths that share segments with existing routes
    // @ts-ignore
    router.setRoutingOption(Avoid.nudgeOrthogonalSegmentsConnectedToShapes, true);

    // Nudge colinear segments that touch at their ends
    // @ts-ignore
    router.setRoutingOption(Avoid.nudgeOrthogonalTouchingColinearSegments, true);

    // Penalise shared paths — forces libavoid to prefer distinct routes
    // @ts-ignore
    router.setRoutingOption(Avoid.penaliseOrthogonalSharedPathsAtConnEnds, true);

    this.avoidRouter = router;
  }

  clean = (element: joint.dia.Element) => {
  }

  // ─── Shape Management ──────────────────────────────────────

  updateShape(element: joint.dia.Element) {
    const Avoid = AvoidLib.getInstance();
    const {x, y, width, height} = element.getBBox();
    const rect = new Avoid.Rectangle(
      new Avoid.Point(x, y),
      new Avoid.Point(x + width, y + height)
    );

    if (this.shapeRefs[element.id]) {
      this.avoidRouter.moveShape(this.shapeRefs[element.id], rect);
      return;
    }

    const shapeRef = new Avoid.ShapeRef(this.avoidRouter, rect);
    this.shapeRefs[element.id] = shapeRef;

    // // Default center pin
    // const centerPin = new Avoid.ShapeConnectionPin(
    //   shapeRef,
    //   defaultPin,
    //   0.5, 0.5, true,
    //   0,
    //   // @ts-ignore
    //   Avoid.ConnDirAll
    // );
    // centerPin.setExclusive(false);

    // const Avoid = AvoidLib.getInstance();

    const pins = [
      // @ts-ignore
      { x: 0,   y: 0.5, dir: Avoid.ConnDirLeft },
      // @ts-ignore
      { x: 1,   y: 0.5, dir: Avoid.ConnDirRight },
      // @ts-ignore
      { x: 0.5, y: 0,   dir: Avoid.ConnDirUp },
      // @ts-ignore
      { x: 0.5, y: 1,   dir: Avoid.ConnDirDown },
    ];
    pins.forEach(p => {
      const pin = new Avoid.ShapeConnectionPin(
        shapeRef, defaultPin, p.x, p.y, true, 0, p.dir
      );
      pin.setExclusive(false);
    });

    // Add pins for ports if they exist
    this.addPortPins(element, shapeRef);
  }

  private addPortPins(element: joint.dia.Element, shapeRef: any) {
    const Avoid = AvoidLib.getInstance();
    const {width, height} = element.size();
    const rect = new joint.g.Rect(0, 0, width, height);

    element.getPortGroupNames().forEach((group: string) => {
      const portsPositions = element.getPortsPositions(group);
      Object.keys(portsPositions).forEach((portId) => {
        const {x, y} = portsPositions[portId];
        const side = rect.sideNearestToPoint({x, y} as joint.g.PlainPoint);
        const pin = new Avoid.ShapeConnectionPin(
          shapeRef,
          // @ts-ignore
          this.getConnectionPinId(element.id, portId),
          x / width,
          y / height,
          true,
          0,
          this.connDirections[side]
        );
        pin.setExclusive(false);
      });
    });
  }

  deleteShape(element: joint.dia.Element) {
    const shapeRef = this.shapeRefs[element.id];
    if (!shapeRef) return;
    this.avoidRouter.deleteShape(shapeRef);
    delete this.shapeRefs[element.id];
  }

  // ─── Connector Management ──────────────────────────────────

  updateConnector(link: joint.dia.Link) {
    const Avoid = AvoidLib.getInstance();

    const {id: sourceId, port: sourcePortId} = link.source() as any;
    const {id: targetId, port: targetPortId} = link.target() as any;

    if (!sourceId || !targetId) {
      this.deleteConnector(link);
      return null;
    }

    const sourceShapeRef = this.shapeRefs[sourceId];
    const targetShapeRef = this.shapeRefs[targetId];
    if (!sourceShapeRef || !targetShapeRef) return null;

    const sourceConnEnd = new Avoid.ConnEnd(
      sourceShapeRef,
      sourcePortId ? this.getConnectionPinId(sourceId, sourcePortId) : defaultPin
    );
    const targetConnEnd = new Avoid.ConnEnd(
      targetShapeRef,
      targetPortId ? this.getConnectionPinId(targetId, targetPortId) : defaultPin
    );

    let connRef = this.edgeRefs[link.id];

    if (connRef) {
      connRef.setSourceEndpoint(sourceConnEnd);
      connRef.setDestEndpoint(targetConnEnd);
      return connRef;
    }

    connRef = new Avoid.ConnRef(this.avoidRouter);
    connRef.setSourceEndpoint(sourceConnEnd);
    connRef.setDestEndpoint(targetConnEnd);
    connRef.setCallback(this.onAvoidConnectorChange.bind(this), connRef);

    this.edgeRefs[link.id] = connRef;
    this.linksByPointer[connRef.g] = link;

    return connRef;
  }

  deleteConnector(link: joint.dia.Link) {
    const connRef = this.edgeRefs[link.id];
    if (!connRef) return;
    this.avoidRouter.deleteConnector(connRef);
    delete this.linksByPointer[connRef.g];
    delete this.edgeRefs[link.id];
  }

  // ─── Routing ───────────────────────────────────────────────

  routeAll(skipTransaction=false) {
    console.log("routeAll (1)");
    this.graph.getElements().forEach((el) => this.updateShape(el));
    console.log("routeAll (2)");
    this.graph.getLinks().forEach((link) => this.updateConnector(link));
    console.log("routeAll (3)");
    this.avoidRouter.processTransaction();
    console.log("routeAll (4)");
  }

  /**
   * Routes every link once the page has shown what it holds: routing a large map takes seconds, during which
   * the page does not respond. Asked again before then, the links are routed only once.
   */
  routeAllSoon() {
    if (this.destroyed || this.routingScheduled) return;
    this.routingScheduled = true;
    this.onRoutingChange?.(true);

    // The next frame is drawn first, the routing starting right after it
    requestAnimationFrame(() => setTimeout(() => {
      this.routingScheduled = false;
      if (this.destroyed) return;
      this.routeAll();
      this.onRoutingChange?.(false);
    }));
  }

  routeLinksOnly() {
    console.log("routeLinksOnly (1)");
    // Update all shapes to their current positions (no JointJS changes)
    this.graph.getElements().forEach((el) => this.updateShape(el));
    console.log("routeLinksOnly (2)");
    // Update all connectors
    this.graph.getLinks().forEach((link) => this.updateConnector(link));
    console.log("routeLinksOnly (3)");
    // Process — this triggers callbacks that update links
    this.avoidRouter.processTransaction();
    console.log("routeLinksOnly (4)");
  }

  public getVertices(link: joint.dia.Link) {
    const vertices: VerticePoint[] = [];
    const connRef = this.edgeRefs[link.id];
    if (!connRef)
      return vertices;

    const route = connRef.displayRoute();

    // Skip first and last points (they are source/target anchors)
    for (let i = 1; i < route.size() - 1; i++) {
      const {x, y} = route.get_ps(i);
      vertices.push({x, y});
    }

    return vertices;
  }

  protected routeLink(link: joint.dia.Link) {
    const connRef = this.edgeRefs[link.id];
    if (!connRef) return;

    const route = connRef.displayRoute();
    const vertices: { x: number, y: number }[] = [];

    // Skip first and last points (they are source/target anchors)
    for (let i = 1; i < route.size() - 1; i++) {
      const {x, y} = route.get_ps(i);
      vertices.push({x, y});
    }

    // Get anchor points from the route
    const sourcePoint = route.get_ps(0);
    const targetPoint = route.get_ps(route.size() - 1);

    const {id: sourceId, port: sourcePortId} = link.source() as any;
    const {id: targetId, port: targetPortId} = link.target() as any;

    const sourceElement = link.getSourceElement();
    const targetElement = link.getTargetElement();

    if (!sourceElement || !targetElement) return;

    const sourceAnchorDelta = this.getAnchorDelta(sourceElement, sourcePortId, sourcePoint);
    const targetAnchorDelta = this.getAnchorDelta(targetElement, targetPortId, targetPoint);

    if (! this.ignoreVerticesSet) {
      link.set({
        vertices,
        source: {
          id: sourceId,
          port: sourcePortId || undefined,
          anchor: {
            name: 'modelCenter',
            args: {dx: sourceAnchorDelta.x, dy: sourceAnchorDelta.y},
          },
        },
        target: {
          id: targetId,
          port: targetPortId || undefined,
          anchor: {
            name: 'modelCenter',
            args: {dx: targetAnchorDelta.x, dy: targetAnchorDelta.y},
          },
        },
        router: null,
      }, {avoidRouter: true});
    }
  }

  private getAnchorDelta(
    element: joint.dia.Element,
    portId: string | null,
    point: { x: number, y: number }
  ) {
    let anchorPosition: joint.g.PlainPoint;
    if (portId) {
      const port = element.getPort(portId);
      if (port) {
        const portPosition = element.getPortsPositions(port.group!)[portId];
        const pos = element.position();
        anchorPosition = {x: pos.x + portPosition.x, y: pos.y + portPosition.y};
      } else {
        anchorPosition = element.getBBox().center();
      }
    } else {
      anchorPosition = element.getBBox().center();
    }
    return {x: point.x - anchorPosition.x, y: point.y - anchorPosition.y};
  }

  // ─── Graph Listeners ───────────────────────────────────────

  private onAvoidConnectorChange(connRefPtr: number) {
    const link = this.linksByPointer[connRefPtr];
    if (!link) return;
    this.routeLink(link);
  }

  // ─── Helpers ───────────────────────────────────────────────

  private getConnectionPinId(elementId: string, portId: string): number {
    const pinKey = `${elementId}:${portId}`;
    if (pinKey in this.pinIds) return this.pinIds[pinKey];
    const pinId = this.id++;
    this.pinIds[pinKey] = pinId;
    return pinId;
  }

  destroy() {
    this.destroyed = true;
    if (this.routingScheduled) {
      this.routingScheduled = false;
      this.onRoutingChange?.(false);
    }
    this.graph.getLinks().forEach((link) => this.deleteConnector(link));
    this.graph.getElements().forEach((el) => this.deleteShape(el));
    this.shapeRefs = {};
    this.edgeRefs = {};
    this.linksByPointer = {};
    this.pinIds = {};
  }
}
